import './Navbar.scss';

import { useState } from 'react';
import { Dropdown, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { CButton } from '@/components/CButton';
import BreadCrumb from '@/components/general/BreadCrump';
import { LanguageSwitcher } from '@/components/general/LanguageSwitcher';
import { Icon } from '@/components/Icon';
import { ThemeToggle } from '@/components/ThemeToggle';
import { toggleMobileSidebar } from '@/core/redux/themeSettingsSlice';
import { useNavbarNotifications } from '@/modules/notifications/_hooks/useNavbarNotifications';
import {
  generateNotificationMessage,
  generateNotificationTitle,
} from '@/modules/notifications/helpers/notificationHelpers';

const { Text } = Typography;

/** The workspace utility row: location (breadcrumb) and the contextual controls. */
const Navbar = ({ showMenuTrigger }) => {
  const { t } = useTranslation(['common', 'sidebar-menu', 'pages/notifications']);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // The persisted flag is named `isMobileSidebarOpen` but has always meant "hidden".
  const isDrawerOpen = !useSelector((state) => state.theme.isMobileSidebarOpen);
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);
  const { notifications, unreadCount, isSignalRConnected } = useNavbarNotifications();
  const unreadLabel = unreadCount > 9 ? '9+' : String(unreadCount);

  const handleViewAllNotifications = () => {
    setNotificationDropdownOpen(false);
    navigate('/notifications');
  };

  const notificationDropdown = (
    <div className="c-notification-dropdown">
      <div className="c-notification-dropdown__head">
        <span className="c-notification-dropdown__title">
          {t('pages/notifications:pages.notifications.navbar.title')}
        </span>
        <span className="c-notification-dropdown__meta">
          {t('pages/notifications:pages.notifications.navbar.unreadCount', { count: unreadCount })}
        </span>
      </div>
      <div className="c-notification-dropdown__list">
        {notifications.length > 0 ? (
          notifications.map((notification) => {
            const params = notification.params || {};
            const title = generateNotificationTitle(notification.typeKey, params, t);
            const message = generateNotificationMessage(notification.typeKey, params, t);

            return (
              <div key={notification.id} className="c-notification-dropdown__item">
                <span
                  className={`c-notification-dropdown__dot ${
                    notification.isRead ? 'c-notification-dropdown__dot--read' : ''
                  }`}
                  aria-hidden="true"
                />
                <span className="c-notification-dropdown__copy">
                  <Text ellipsis={{ tooltip: title }} strong>
                    {title}
                  </Text>
                  <Text ellipsis={{ tooltip: message }} type="secondary">
                    {message}
                  </Text>
                </span>
              </div>
            );
          })
        ) : (
          <div className="c-notification-dropdown__empty">
            <Text type="secondary">
              {t('pages/notifications:pages.notifications.navbar.noUnreadNotifications')}
            </Text>
          </div>
        )}
      </div>
      <CButton
        variant="ghost"
        block
        className="c-notification-dropdown__footer"
        endIcon={<Icon name="arrowRight" size={16} />}
        onClick={handleViewAllNotifications}
      >
        {t('pages/notifications:pages.notifications.navbar.viewAllNotifications')}
      </CButton>
    </div>
  );

  return (
    <header className="c-navbar">
      {showMenuTrigger && (
        <button
          type="button"
          aria-expanded={isDrawerOpen}
          aria-haspopup="dialog"
          className="c-navbar__menu-trigger"
          onClick={() => dispatch(toggleMobileSidebar())}
        >
          <Icon name="menu" />
          <span>{t('sidebar-menu:sidebar-menu.menu')}</span>
        </button>
      )}
      <div className="c-navbar__breadcrumb">
        <BreadCrumb />
      </div>
      <div className="c-navbar__actions">
        <LanguageSwitcher />
        <ThemeToggle variant="utility" />
        <Dropdown
          styles={{ root: { padding: 0 } }}
          popupRender={() => notificationDropdown}
          placement="bottomRight"
          trigger={['click']}
          open={notificationDropdownOpen}
          onOpenChange={setNotificationDropdownOpen}
        >
          <button
            type="button"
            className={`c-notification-trigger${
              isSignalRConnected ? '' : ' c-notification-trigger--offline'
            }`}
            aria-label={t('pages/notifications:pages.notifications.navbar.viewAllNotifications')}
          >
            <Icon name="bell" className="c-notification-trigger__icon" />
            {unreadCount > 0 && (
              <span className="c-notification-trigger__pill" aria-hidden="true">
                {unreadLabel}
              </span>
            )}
          </button>
        </Dropdown>
      </div>
    </header>
  );
};

export default Navbar;
